import { Reducer } from "redux"
import { InterfaceType, AuthUserType } from "../../../Actions/Types"
import { updateAt } from "../../../Helpers/Functions/reducer"
import { UserAction } from "../../Auth/UserReducer"
import { RouterAction, LOCATION_CHANGE } from "connected-react-router"
import {
    UploadQueueItem,
    UploadStatus,
    isQueuedOrFailed,
    isInProgress
} from "../../../Models/Client/UploadQueueItem"

export interface UploaderState {
    [stateKey: string]: UploadQueueItem[] | undefined
}

interface UploaderEnqueueUploadAction {
    type: InterfaceType.uploader_enqueueUploads
    payload: {
        stateKey: string
        files: File[]
    }
}

interface UploaderStartUploadAction {
    type: InterfaceType.uploader_startUpload
    payload: {
        stateKey: string
        name: string
    }
}

interface UploaderFinishUploadAction {
    type: InterfaceType.uploader_finishUploads
    payload: {
        stateKey: string
        name: string
        url: string
    }
}

interface UploaderFailUploadAction {
    type: InterfaceType.uploader_failUploads
    payload: {
        stateKey: string
        name: string
        reason: string
    }
}

interface UploaderRemoveQueueItemAction {
    type: InterfaceType.uploader_removeQueueItem
    payload: {
        stateKey: string
        name: string
    }
}

interface UploaderUploadProgressAction {
    type: InterfaceType.uploader_progress
    payload: {
        stateKey: string
        name: string
        progress: number
    }
}

export type UploaderAction =
    | UploaderEnqueueUploadAction
    | UploaderStartUploadAction
    | UploaderFinishUploadAction
    | UploaderFinishUploadAction
    | UploaderFailUploadAction
    | UploaderRemoveQueueItemAction
    | UploaderUploadProgressAction

const initialState: UploaderState = {}

export const UploaderReducer: Reducer<
    UploaderState,
    UploaderAction | UserAction | RouterAction
> = (state = initialState, action): UploaderState => {
    switch (action.type) {
        case LOCATION_CHANGE:
            Object.values(state).forEach(uploaderState => {
                uploaderState?.forEach(item => {
                    URL.revokeObjectURL(item.objectUrl)
                })
            })
            return initialState
        case InterfaceType.uploader_enqueueUploads:
            return updateAt(state, action.payload.stateKey, () =>
                (state[action.payload.stateKey] || []).concat(
                    action.payload.files.map(file => ({
                        status: UploadStatus.Queued,
                        name: file.name,
                        objectUrl: URL.createObjectURL(file)
                    }))
                )
            )
        case InterfaceType.uploader_startUpload:
            return updateAt(state, action.payload.stateKey, queueItems =>
                (queueItems || []).map(item =>
                    isQueuedOrFailed(item) && item.name === action.payload.name
                        ? {
                              ...item,
                              status: UploadStatus.InProgress,
                              progress: 0
                          }
                        : item
                )
            )
        case InterfaceType.uploader_finishUploads:
            return updateAt(state, action.payload.stateKey, queueItems =>
                (queueItems || []).map(item => {
                    if (
                        isInProgress(item) &&
                        item.name === action.payload.name
                    ) {
                        return {
                            ...item,
                            status: UploadStatus.Finished,
                            url: action.payload.url
                        }
                    }

                    return item
                })
            )
        case InterfaceType.uploader_failUploads:
            return updateAt(state, action.payload.stateKey, queueItems =>
                (queueItems || []).map(item =>
                    isInProgress(item) && item.name === action.payload.name
                        ? {
                              ...item,
                              status: UploadStatus.Failed,
                              reason: action.payload.reason
                          }
                        : item
                )
            )
        case InterfaceType.uploader_removeQueueItem:
            return updateAt(state, action.payload.stateKey, queueItems =>
                (queueItems || []).flatMap(item => {
                    if (item.name === action.payload.name) {
                        URL.revokeObjectURL(item.objectUrl)
                        return []
                    }

                    return [item]
                })
            )
        case InterfaceType.uploader_progress:
            return updateAt(state, action.payload.stateKey, queueItems =>
                (queueItems || []).map(item =>
                    isInProgress(item) && item.name === action.payload.name
                        ? {
                              ...item,
                              progress: action.payload.progress
                          }
                        : item
                )
            )
        case AuthUserType.signedOut:
            return initialState
        default:
            return state
    }
}
